import type { CountdownColorTiers, ScheduleItemDto } from "../hooks/use-live-extended-display-data";
import { DEFAULT_COUNTDOWN_COLOR_TIERS } from "../hooks/use-live-extended-display-data";

/**
 * Ported near-verbatim from `apps/desktop`'s own
 * `components/extended-display/extended-display-view.tsx` — same Tailwind
 * classes (Tailwind v3 is fully wired in this app via
 * `src/styles/globals.css`'s `@tailwind base/components/utilities`), a
 * deliberate exception to `routes/control.tsx`/`routes/display.$outputId.tsx`'s
 * own "plain inline styles" convention: hand-porting hundreds of
 * `clamp()`/`cqw` expressions into inline style objects would be pure risk
 * for zero benefit when the entire point is pixel parity with the real
 * Extended Display. Used only when a Browser Display output's own
 * `config.displayMode === "mirror"` (`routes/display.$outputId.tsx`'s own
 * branch) — every other output keeps today's existing themed rendering
 * untouched.
 */

export type ClockFormat = "12h" | "24h";
export type DisplayMode = "clock" | "timer";

export interface OverlayState {
  readonly visible: boolean;
  readonly text: string;
  readonly blink: boolean;
  /** `"fullscreen"` (default, unchanged behavior) takes over the whole screen; `"lower"` renders a bottom banner strip instead, leaving the timer/clock content showing above it. */
  readonly style: "fullscreen" | "lower";
}

export interface DisplayLabels {
  readonly actualTime: string;
  readonly nextItem: string;
  readonly currentItem: string;
  readonly noNextItem: string;
}

export const DEFAULT_DISPLAY_LABELS: DisplayLabels = {
  actualTime: "ACTUAL TIME",
  nextItem: "NEXT ITEM",
  currentItem: "CURRENT ITEM",
  noNextItem: "No Next Item",
};

const MIN_5 = 5 * 60_000;
const MIN_10 = 10 * 60_000;
const MIN_20 = 20 * 60_000;

/** Mirrors `apps/desktop`'s `lib/timer/countdown-color-tiers.ts`'s `resolveCountdownZone` — same tier boundaries, same fallback for sub-2-minute items. */
function resolveCountdownZone(
  durationMs: number,
  remainingMs: number,
  tiers: CountdownColorTiers,
): "normal" | "warning" | "critical" {
  if (durationMs < MIN_5) {
    return remainingMs <= tiers.countdownTier1RedAtMs ? "critical" : "normal";
  }
  if (durationMs < MIN_10) {
    return remainingMs <= tiers.countdownTier2RedAtMs ? "critical" : "normal";
  }
  if (durationMs < MIN_20) {
    if (remainingMs <= tiers.countdownTier3RedAtMs) return "critical";
    if (remainingMs <= tiers.countdownTier3YellowAtMs) return "warning";
    return "normal";
  }
  if (remainingMs <= tiers.countdownTier4RedAtMs) return "critical";
  if (remainingMs <= tiers.countdownTier4YellowAtMs) return "warning";
  return "normal";
}

export interface ExtendedDisplayViewProps {
  now: Date;
  currentItem: ScheduleItemDto | null;
  nextItem: ScheduleItemDto | null;
  remainingSeconds: number;
  elapsedSeconds: number;
  totalSeconds: number;
  progressPercent: number;
  statusLabel: string;
  overlay: OverlayState;
  blackScreen: boolean;
  displayMode: DisplayMode;
  preferredText: string;
  locale: string;
  labels: DisplayLabels;
  fontFamily: string;
  fontSizeScale: number;
  clockFormat?: ClockFormat;
  countdownColorTiers?: CountdownColorTiers;
  /** A genuine, untimed count-up/session (`useLiveExtendedDisplayData`'s own `isUntimedCountUp`) — shows `elapsedSeconds` instead of `remainingSeconds` and skips countdown-zone coloring entirely (there's no target to be "near"). Mirrors `apps/desktop`'s own `ExtendedDisplayView`'s identically-named prop. */
  isCountUp?: boolean;
}

const ZONE_COLOR: Record<"warning" | "critical", string> = {
  warning: "#eab308",
  critical: "#ef4444",
};

const formatTimer = (seconds: number) => {
  const sign = seconds < 0 ? "-" : "";
  const absolute = Math.abs(seconds);
  const hours = Math.floor(absolute / 3600);
  const minutes = Math.floor((absolute % 3600) / 60);
  const remainingSeconds = absolute % 60;

  const pad = (value: number) => String(value).padStart(2, "0");

  if (hours > 0) {
    return `${sign}${hours}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }

  return `${sign}${pad(minutes)}:${pad(remainingSeconds)}`;
};

const formatClock = (date: Date, locale: string, clockFormat: ClockFormat) =>
  date.toLocaleTimeString(locale, {
    hour12: clockFormat === "12h",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatDateLine = (date: Date, locale: string) => {
  const day = date.toLocaleDateString(locale, { day: "2-digit" });
  const month = date.toLocaleDateString(locale, { month: "long" });
  const year = date.toLocaleDateString(locale, { year: "numeric" });
  const weekday = date.toLocaleDateString(locale, { weekday: "short" });

  return `${day} ${month} ${year} - ${weekday}`.toUpperCase();
};

export const formatDisplayTime = (date: Date, locale: string, clockFormat: ClockFormat) =>
  date.toLocaleTimeString(locale, {
    hour12: clockFormat === "12h",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const clockDigitsFontSize = (clockFormat: ClockFormat): string => {
  const scale = clockFormat === "12h" ? 0.75 : 1;
  return `clamp(${9 * scale}rem, ${20 * scale}vw, ${22 * scale}rem)`;
};

const actualTimeFontSize = (clockFormat: ClockFormat): string => {
  const scale = clockFormat === "12h" ? 0.85 : 1;
  return `clamp(${1.75 * scale}rem, ${4 * scale}vw, ${4.25 * scale}rem)`;
};

/**
 * Tuned for a short "MM:SS"/"H:MM:SS" string (5 characters, e.g.
 * "12:34") — a longer one (deep overtime past 100 hours, or an
 * hours-and-tens-of-minutes duration with a leading "-") runs past both
 * edges of the frame at that same fixed size, since rendered width
 * scales with character count at any fixed font size. Scaling the whole
 * clamp range down together (never up) keeps the whole string inside the
 * frame regardless of digit count or sign, whether the timer is still
 * counting down or has gone into overtime. Mirrors `apps/desktop`'s own
 * `countdownFontSize`.
 */
function countdownFontSize(digitLength: number): string {
  const scale = Math.min(1, 5 / Math.max(digitLength, 1));
  return `clamp(${9 * scale}rem, ${26 * scale}vw, ${31 * scale}rem)`;
}

export function ExtendedDisplayView({
  now,
  currentItem,
  nextItem,
  remainingSeconds,
  elapsedSeconds,
  totalSeconds,
  overlay,
  blackScreen,
  displayMode,
  preferredText,
  locale,
  labels,
  fontFamily,
  fontSizeScale,
  clockFormat = "12h",
  countdownColorTiers = DEFAULT_COUNTDOWN_COLOR_TIERS,
  isCountUp = false,
}: ExtendedDisplayViewProps): React.ReactElement {
  if (blackScreen) {
    return <div className="h-full w-full bg-black" />;
  }

  if (overlay.visible && overlay.style === "fullscreen") {
    return (
      <div
        className="relative flex h-full w-full items-center justify-center bg-[#080b15] p-8 text-white"
        style={{
          minHeight: 0,
          fontFamily,
          transform: `scale(${fontSizeScale})`,
          transformOrigin: "center",
        }}
      >
        <div
          className="text-center"
          style={{
            fontFamily,
            fontSize: 120,
            lineHeight: 1.05,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            animation: overlay.blink ? "displayBlink 1s step-start infinite" : undefined,
            textShadow: "0 0 28px rgba(255,255,255,0.16)",
            maxWidth: "90%",
            wordBreak: "break-word",
            transform: `scale(${fontSizeScale})`,
            transformOrigin: "center",
          }}
        >
          {overlay.text}
        </div>
      </div>
    );
  }

  // Mirrors `apps/desktop`'s own identical `lowerBanner` (including its
  // `#ef4444` — the same red `--color-status-critical` resolves to over
  // there; this app has no equivalent design token, so the raw hex is
  // repeated here rather than invented fresh) — docks a banner strip at
  // the bottom of whatever's already rendering below (clock or timer
  // mode) instead of taking over the screen. The whole banner — red box
  // and text together — blinks as one unit.
  const lowerBanner =
    overlay.visible && overlay.style === "lower" ? (
      <div
        className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-[#ef4444] px-8 text-center font-black uppercase text-white"
        style={{
          minHeight: "16vh",
          animation: overlay.blink ? "displayBlink 1s step-start infinite" : undefined,
        }}
      >
        <span
          style={{
            fontFamily,
            fontSize: "clamp(1.5rem, 3.2vw, 3.5rem)",
            lineHeight: 1.15,
            letterSpacing: "0.06em",
            textShadow: "0 0 14px rgba(0,0,0,0.35)",
            wordBreak: "break-word",
          }}
        >
          {overlay.text}
        </span>
      </div>
    ) : null;

  if (displayMode === "clock") {
    return (
      <div
        className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#070a14] p-[6vw] font-black uppercase text-white"
        style={{ fontFamily, transform: `scale(${fontSizeScale})`, transformOrigin: "center" }}
      >
        <div className="flex max-w-[92%] flex-col items-center text-center">
          <div
            className="whitespace-nowrap tracking-[0.18em]"
            style={{ fontSize: "clamp(2.25rem, 4.2vw, 5rem)" }}
          >
            {formatDateLine(now, locale)}
          </div>

          <div
            className="mt-[4cqh] whitespace-nowrap leading-none tracking-[0.02em]"
            style={{ fontSize: clockDigitsFontSize(clockFormat) }}
          >
            {formatDisplayTime(now, locale, clockFormat)}
          </div>

          <div
            className="mt-[5cqh] max-w-full truncate whitespace-nowrap tracking-[0.18em]"
            style={{ fontSize: "clamp(1.75rem, 3.2vw, 4rem)" }}
          >
            {preferredText}
          </div>
        </div>
        {lowerBanner}
      </div>
    );
  }

  // An untimed count-up/session has no target to be "near" — skip the
  // zone computation entirely rather than feed it `remainingSeconds`,
  // which `useLiveExtendedDisplayData` reports as 0 (not `Infinity`, to
  // keep `formatTimer` below from rendering "Infinity:NaN:NaN") and would
  // otherwise resolve to "critical" for every count-up/session timer.
  const countdownZone = isCountUp
    ? "normal"
    : resolveCountdownZone(totalSeconds * 1000, remainingSeconds * 1000, countdownColorTiers);
  const countdownColor = countdownZone === "normal" ? undefined : ZONE_COLOR[countdownZone];
  const timerText = formatTimer(isCountUp ? elapsedSeconds : remainingSeconds);

  return (
    <div
      className="relative flex h-full w-full flex-col overflow-hidden bg-[#080b15] px-[10vw] py-[8vh] font-black uppercase text-white"
      style={{ fontFamily, transform: `scale(${fontSizeScale})`, transformOrigin: "center" }}
    >
      <div className="flex items-start justify-between gap-8">
        <div className="min-w-0 text-left">
          <div className="tracking-[0.08em]" style={{ fontSize: "clamp(0.75rem,1.7vw,1.75rem)" }}>
            {labels.actualTime}
          </div>
          <div
            className="mt-2 whitespace-nowrap leading-none tracking-[0.01em]"
            style={{ fontSize: actualTimeFontSize(clockFormat) }}
          >
            {formatClock(now, locale, clockFormat)}
          </div>
        </div>

        <div className="min-w-0 max-w-[38vw] text-right">
          <div className="tracking-[0.08em]" style={{ fontSize: "clamp(0.75rem,1.7vw,1.75rem)" }}>
            {labels.nextItem}
          </div>
          <div
            className="mt-3 truncate leading-none tracking-[0.01em]"
            style={{ fontSize: "clamp(1.75rem,4vw,4.25rem)" }}
          >
            {nextItem?.title || labels.noNextItem}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center pb-[8vh] text-center">
        <div
          className="whitespace-nowrap leading-[0.85] tracking-[0.01em] transition-colors duration-500"
          style={{ fontSize: countdownFontSize(timerText.length), color: countdownColor }}
        >
          {timerText}
        </div>

        <div
          className="mt-[5vh] max-w-[82vw] truncate leading-none tracking-[0.03em]"
          style={{ fontSize: "clamp(2rem,5vw,5.25rem)" }}
        >
          {currentItem?.title || labels.currentItem}
        </div>
        {currentItem?.speaker ? (
          <div
            className="mt-4 max-w-[70vw] truncate font-bold tracking-[0.12em] text-white/70"
            style={{ fontSize: "clamp(0.9rem,1.8vw,1.75rem)" }}
          >
            {currentItem.speaker}
          </div>
        ) : null}
      </div>
      {lowerBanner}
    </div>
  );
}
